import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import {
  salesInvoiceService,
  ProductSalesDTO,
  ProductInvoiceSummaryDTO,
  MenuItemSalesDTO,
  SalesByProductResultDTO,
} from '../../../../services/salesInvoiceService';
import {
  ShoppingBag, DollarSign, Package, Receipt,
  TrendingUp, Trophy, Search, FileText,
  Download, Eye, ArrowLeft, ChevronDown, ChevronUp,
  UtensilsCrossed,
} from 'lucide-react';
import './SalesReportCommon.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const today    = () => new Date().toISOString().split('T')[0];
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split('T')[0];
};

const statusBadgeClass = (s: string) => {
  const map: Record<string, string> = {
    Draft: 'ims-badge ims-badge-draft',        Issued: 'ims-badge ims-badge-confirmed',
    Paid:  'ims-badge ims-badge-approved',      PartiallyPaid: 'ims-badge ims-badge-pending',
    Overdue: 'ims-badge ims-badge-rejected',    Voided: 'ims-badge ims-badge-closed',
    Cancelled: 'ims-badge ims-badge-closed',    Unpaid: 'ims-badge ims-badge-draft',
  };
  return map[s] ?? 'ims-badge ims-badge-draft';
};

// ── Component ─────────────────────────────────────────────────────────────────

const SalesByProductReportPage: React.FC = () => {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  const fmt = (n: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(n);

  // ── Filter state
  const [filters, setFilters] = useState({
    startDate: monthAgo(), startTime: '00:00',
    endDate:   today(),    endTime:   '23:59',
  });

  type SortKey = 'revenue' | 'quantity' | 'invoices';
  const [sortBy, setSortBy] = useState<SortKey>('revenue');

  // ── Result state
  const [hasSearched,  setHasSearched]  = useState(false);
  const [searching,    setSearching]    = useState(false);
  const [products,     setProducts]     = useState<ProductSalesDTO[]>([]);
  const [menuItems,    setMenuItems]    = useState<MenuItemSalesDTO[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedMenuRows, setExpandedMenuRows] = useState<Set<number>>(new Set());

  // ── PDF state
  const [viewingPdf,     setViewingPdf]     = useState<Set<number>>(new Set());
  const [downloadingPdf, setDownloadingPdf] = useState<Set<number>>(new Set());
  const [pdfError,       setPdfError]       = useState<Record<number, string>>({});

  // ── Export state
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // ── Derived: sorted + totals
  const sortedProducts = [...products].sort((a, b) => {
    if (sortBy === 'quantity') return b.totalQuantity - a.totalQuantity;
    if (sortBy === 'invoices') return b.numberOfInvoices - a.numberOfInvoices;
    return b.totalRevenue - a.totalRevenue;
  });

  const sortedMenuItems = [...menuItems].sort((a, b) => {
    if (sortBy === 'quantity') return b.totalQuantity - a.totalQuantity;
    if (sortBy === 'invoices') return b.numberOfOrders - a.numberOfOrders;
    return b.totalRevenue - a.totalRevenue;
  });

  const productTotals = products.reduce(
    (acc, p) => ({ qty: acc.qty + p.totalQuantity, revenue: acc.revenue + p.totalRevenue, invoices: acc.invoices + p.numberOfInvoices }),
    { qty: 0, revenue: 0, invoices: 0 },
  );

  const menuItemTotals = menuItems.reduce(
    (acc, m) => ({ qty: acc.qty + m.totalQuantity, revenue: acc.revenue + m.totalRevenue, orders: acc.orders + m.numberOfOrders }),
    { qty: 0, revenue: 0, orders: 0 },
  );

  const totals = {
    qty:      productTotals.qty + menuItemTotals.qty,
    revenue:  productTotals.revenue + menuItemTotals.revenue,
    invoices: productTotals.invoices,
  };

  const uniqueInvoices = new Set(products.flatMap(p => p.invoices.map(i => i.invoiceId))).size;

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSearch = async () => {
    setSearching(true); setHasSearched(true);
    setExpandedRows(new Set()); setExpandedMenuRows(new Set()); setPdfError({});
    try {
      const res    = await salesInvoiceService.searchByProduct(filters);
      const result: SalesByProductResultDTO = (res.data as any)?.data ?? res.data ?? { products: [], menuItems: [] };
      setProducts(result.products ?? []);
      setMenuItems(result.menuItems ?? []);
    } catch {
      setProducts([]);
      setMenuItems([]);
    } finally {
      setSearching(false);
    }
  };

  const handleFilterChange = (field: string, value: string) =>
    setFilters(prev => ({ ...prev, [field]: value }));

  const toggleRow = (productId: number) =>
    setExpandedRows(prev => {
      const s = new Set(prev);
      s.has(productId) ? s.delete(productId) : s.add(productId);
      return s;
    });

  const toggleMenuRow = (menuItemId: number) =>
    setExpandedMenuRows(prev => {
      const s = new Set(prev);
      s.has(menuItemId) ? s.delete(menuItemId) : s.add(menuItemId);
      return s;
    });

  const handleViewPdf = async (inv: ProductInvoiceSummaryDTO) => {
    if (viewingPdf.has(inv.invoiceId)) return;
    setViewingPdf(prev => new Set(prev).add(inv.invoiceId));
    try {
      await salesInvoiceService.viewInvoicePdf(inv.invoiceId, inv.invoiceNumber);
    } catch {
      setPdfError(prev => ({ ...prev, [inv.invoiceId]: t('common.loadError', 'Failed to open PDF') }));
    } finally {
      setViewingPdf(prev => { const s = new Set(prev); s.delete(inv.invoiceId); return s; });
    }
  };

  const handleDownloadPdf = async (inv: ProductInvoiceSummaryDTO) => {
    if (downloadingPdf.has(inv.invoiceId)) return;
    setDownloadingPdf(prev => new Set(prev).add(inv.invoiceId));
    try {
      await salesInvoiceService.downloadInvoicePdf(inv.invoiceId, inv.invoiceNumber);
    } catch {
      setPdfError(prev => ({ ...prev, [inv.invoiceId]: t('common.loadError', 'Failed to download PDF') }));
    } finally {
      setDownloadingPdf(prev => { const s = new Set(prev); s.delete(inv.invoiceId); return s; });
    }
  };

  const handleExportCsv = async () => {
    if (exportingCsv) return;
    setExportingCsv(true);
    try {
      const stamp = `${filters.startDate}_${filters.endDate}`;
      await salesInvoiceService.exportProductCsv(filters, `sales-by-product_${stamp}.csv`);
    } finally { setExportingCsv(false); }
  };

  const handleExportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      const stamp = `${filters.startDate}_${filters.endDate}`;
      await salesInvoiceService.exportProductPdf(filters, `sales-by-product_${stamp}.pdf`);
    } finally { setExportingPdf(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="ssr-page">

        {/* ── Hero ──────────────────────────────────────────────────── */}
        <div className="ssr-hero">
          <div>
            <h1 className="ssr-hero-title">
              <ShoppingBag size={22} strokeWidth={1.8} />
              {t('salesReports.byProductTitle', 'Sales by Product')}
            </h1>
            <p className="ssr-hero-sub">
              {t('salesReports.byProductSubtitle', 'Aggregate product sales within a date and time range')}
            </p>
          </div>

          <div className="ssr-hero-actions">
            {hasSearched && products.length > 0 && (
              <div className="ssr-export-group">
                <button className="ssr-export-btn ssr-export-csv" onClick={handleExportCsv} disabled={exportingCsv}>
                  <FileText size={13} strokeWidth={2} />
                  {exportingCsv ? t('common.exporting', 'Exporting…') : 'Export CSV'}
                </button>
                <button className="ssr-export-btn ssr-export-pdf" onClick={handleExportPdf} disabled={exportingPdf}>
                  <Download size={13} strokeWidth={2} />
                  {exportingPdf ? t('common.exporting', 'Exporting…') : 'Export PDF'}
                </button>
              </div>
            )}
            <button className="ssr-back-btn" onClick={() => navigate('/reports/sales')}>
              <ArrowLeft size={14} strokeWidth={2} />
              {t('common.back', 'Back')}
            </button>
          </div>
        </div>

        {/* ── Filter card ───────────────────────────────────────────── */}
        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title">
              <Search size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {t('salesReports.dateRangeFilter', 'Date Range Filter')}
            </span>
          </div>
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="ssr-filter-bar">

              <div className="ssr-filter-group">
                <span className="ssr-filter-label">{t('salesReports.fromDateTime', 'From date / time')}</span>
                <div className="ssr-filter-row">
                  <input type="date" className="ims-form-control"
                    value={filters.startDate}
                    onChange={e => handleFilterChange('startDate', e.target.value)} />
                  <input type="time" className="ims-form-control ssr-time-input"
                    value={filters.startTime}
                    onChange={e => handleFilterChange('startTime', e.target.value)} />
                </div>
              </div>

              <div className="ssr-filter-group">
                <span className="ssr-filter-label">{t('salesReports.toDateTime', 'To date / time')}</span>
                <div className="ssr-filter-row">
                  <input type="date" className="ims-form-control"
                    value={filters.endDate}
                    onChange={e => handleFilterChange('endDate', e.target.value)} />
                  <input type="time" className="ims-form-control ssr-time-input"
                    value={filters.endTime}
                    onChange={e => handleFilterChange('endTime', e.target.value)} />
                </div>
              </div>

              <div className="ssr-filter-group" style={{ maxWidth: 170 }}>
                <span className="ssr-filter-label">{t('salesReports.sortBy', 'Sort by')}</span>
                <select className="ims-form-control" value={sortBy}
                  onChange={e => setSortBy(e.target.value as SortKey)}>
                  <option value="revenue">{t('salesReports.sortRevenue', 'Revenue (high → low)')}</option>
                  <option value="quantity">{t('salesReports.sortQty', 'Qty Sold (high → low)')}</option>
                  <option value="invoices">{t('salesReports.sortInvoices', 'Invoice Count')}</option>
                </select>
              </div>

              <button
                className="ims-btn ims-btn-primary"
                onClick={handleSearch}
                disabled={searching}
                style={{ alignSelf: 'flex-end' }}
              >
                {searching ? (
                  <><span className="ssr-spinner" /> {t('common.searching', 'Searching…')}</>
                ) : (
                  <><Search size={14} strokeWidth={2.5} /> {t('common.search', 'Search')}</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── Initial prompt ────────────────────────────────────────── */}
        {!hasSearched && (
          <div className="ssr-prompt">
            <Search size={48} strokeWidth={1} />
            <p>
              {t('salesReports.searchPrompt', 'Set a date range and click')}{' '}
              <strong>{t('common.search', 'Search')}</strong>{' '}
              {t('salesReports.searchPromptProductEnd', 'to load product sales.')}
            </p>
          </div>
        )}

        {/* ── Results ───────────────────────────────────────────────── */}
        {hasSearched && !searching && (
          <>
            {/* ── KPI row ──────────────────────────────────────────── */}
            {(products.length > 0 || menuItems.length > 0) && (
              <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-total"><ShoppingBag size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value">{products.length}</span>
                    <span className="ims-stat-label">{t('salesReports.productsSold', 'Products Sold')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-rate"><UtensilsCrossed size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value">{menuItems.length}</span>
                    <span className="ims-stat-label">{t('salesReports.menuItemsSold', 'Menu Items Sold')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-sales"><DollarSign size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(totals.revenue)}</span>
                    <span className="ims-stat-label">{t('salesReports.totalRevenue', 'Total Revenue')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-avg"><Package size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value">{totals.qty.toLocaleString()}</span>
                    <span className="ims-stat-label">{t('salesReports.totalQtySold', 'Total Qty Sold')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-rate"><Receipt size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value">{uniqueInvoices}</span>
                    <span className="ims-stat-label">{t('salesReports.invoicesCovered', 'Invoices Covered')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-paid"><TrendingUp size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>
                      {fmt((products.length + menuItems.length) > 0 ? totals.revenue / (products.length + menuItems.length) : 0)}
                    </span>
                    <span className="ims-stat-label">{t('salesReports.avgRevenuePerItem', 'Avg Revenue / Item')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-purple"><Trophy size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {sortedProducts[0]?.productName ?? '—'}
                    </span>
                    <span className="ims-stat-label">{t('salesReports.topProduct', 'Top Product')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* ── Product table ─────────────────────────────────────── */}
            {products.length === 0 ? (
              <div className="ims-card">
                <div className="ssr-empty">
                  {t('salesReports.noProducts', 'No product sales found for the selected period.')}
                </div>
              </div>
            ) : (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <ShoppingBag size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    {t('salesReports.products', 'Products')}
                    <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--ims-text-muted)', marginInlineStart: 6 }}>
                      ({products.length} {products.length !== 1
                        ? t('salesReports.records', 'records')
                        : t('salesReports.record', 'record')})
                    </span>
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="ssr-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>#</th>
                        <th>{t('salesReports.productName', 'Product Name')}</th>
                        <th>{t('salesReports.sku', 'SKU')}</th>
                        <th className="num">{t('salesReports.qtySold', 'Qty Sold')}</th>
                        <th className="num">{t('salesReports.revenue', 'Revenue')}</th>
                        <th className="num">{t('salesReports.avgUnitPrice', 'Avg Unit Price')}</th>
                        <th className="num">{t('salesReports.invoiceCount', '# Invoices')}</th>
                        <th>{t('salesReports.colActions', 'Actions')}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {sortedProducts.map((product, idx) => (
                        <React.Fragment key={product.productId}>

                          {/* Product summary row */}
                          <tr>
                            <td className="ssr-row-num">{idx + 1}</td>
                            <td style={{ fontWeight: 600 }}>{product.productName}</td>
                            <td><span className="ssr-sku">{product.productSKU}</span></td>
                            <td className="num">{product.totalQuantity.toLocaleString()}</td>
                            <td className="num ssr-revenue-value">{fmt(product.totalRevenue)}</td>
                            <td className="num">{fmt(product.averagePrice)}</td>
                            <td className="num">{product.numberOfInvoices}</td>
                            <td>
                              <div className="ssr-row-actions">
                                <button
                                  className="ssr-btn-row ssr-btn-detail"
                                  onClick={() => toggleRow(product.productId)}
                                  title={t('salesReports.toggleInvoices', 'Show / hide invoices')}
                                >
                                  {expandedRows.has(product.productId)
                                    ? <><ChevronUp size={11} /> {t('salesReports.hide', 'Hide')}</>
                                    : <><ChevronDown size={11} /> {t('salesReports.invoices', 'Invoices')}</>
                                  }
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded invoices sub-table */}
                          {expandedRows.has(product.productId) && (
                            <tr className="ssr-detail-row">
                              <td colSpan={8}>
                                <div className="ssr-detail-inner">
                                  <h4 className="ssr-detail-title">
                                    {t('salesReports.invoicesContaining', 'Invoices containing')} "{product.productName}"
                                    &nbsp;—&nbsp;{product.invoices.length}{' '}
                                    {product.invoices.length !== 1
                                      ? t('salesReports.invoicesLower', 'invoices')
                                      : t('salesReports.invoiceLower', 'invoice')}
                                  </h4>

                                  {product.invoices.length === 0 ? (
                                    <p style={{ color: 'var(--ims-text-muted)', fontSize: 12 }}>
                                      {t('salesReports.noInvoices', 'No invoices found.')}
                                    </p>
                                  ) : (
                                    <table className="ssr-lines-table">
                                      <thead>
                                        <tr>
                                          <th>{t('salesReports.colInvoice', 'Invoice #')}</th>
                                          <th>{t('salesReports.colCustomer', 'Customer')}</th>
                                          <th>{t('salesReports.colDate', 'Date')}</th>
                                          <th>{t('salesReports.colStatus', 'Status')}</th>
                                          <th>{t('salesReports.colPayment', 'Payment')}</th>
                                          <th className="num">{t('salesReports.qty', 'Qty')}</th>
                                          <th className="num">{t('salesReports.lineTotal', 'Line Total')}</th>
                                          <th>{t('salesReports.colActions', 'Actions')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {product.invoices.map(inv => (
                                          <tr key={inv.invoiceId}>
                                            <td style={{ fontWeight: 600 }}>
                                              <span className="ssr-inv-num">{inv.invoiceNumber}</span>
                                            </td>
                                            <td>{inv.customerName}</td>
                                            <td>{new Date(inv.invoiceDate).toLocaleDateString()}</td>
                                            <td>
                                              <span className={statusBadgeClass(inv.status)}>{inv.status}</span>
                                            </td>
                                            <td>
                                              <span className={statusBadgeClass(inv.paymentStatus)}>{inv.paymentStatus}</span>
                                            </td>
                                            <td className="num">{inv.quantity.toLocaleString()}</td>
                                            <td className="num" style={{ fontWeight: 600 }}>{fmt(inv.lineTotal)}</td>
                                            <td>
                                              <div className="ssr-row-actions">
                                                <button
                                                  className="ssr-btn-row ssr-btn-view"
                                                  onClick={() => handleViewPdf(inv)}
                                                  disabled={viewingPdf.has(inv.invoiceId)}
                                                  title={t('salesReports.viewPdf', 'Open PDF in new tab')}
                                                >
                                                  {viewingPdf.has(inv.invoiceId)
                                                    ? '…'
                                                    : <><Eye size={11} /> {t('salesReports.view', 'View')}</>}
                                                </button>
                                                <button
                                                  className="ssr-btn-row ssr-btn-download"
                                                  onClick={() => handleDownloadPdf(inv)}
                                                  disabled={downloadingPdf.has(inv.invoiceId)}
                                                  title={t('salesReports.downloadPdf', 'Download PDF')}
                                                >
                                                  {downloadingPdf.has(inv.invoiceId)
                                                    ? '…'
                                                    : <><Download size={11} /> PDF</>}
                                                </button>
                                              </div>
                                              {pdfError[inv.invoiceId] && (
                                                <div className="ssr-row-err">{pdfError[inv.invoiceId]}</div>
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}

                                  {/* Product sub-totals */}
                                  <div className="ssr-detail-totals">
                                    <span>
                                      {t('salesReports.totalQty', 'Total qty')}:{' '}
                                      <strong>{product.totalQuantity.toLocaleString()}</strong>
                                    </span>
                                    <span className="ssr-detail-total-grand">
                                      {t('salesReports.totalRevenue', 'Total revenue')}:{' '}
                                      <strong>{fmt(product.totalRevenue)}</strong>
                                    </span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>

                    {/* Grand total footer */}
                    <tfoot>
                      <tr>
                        <td></td>
                        <td colSpan={2}>
                          {t('salesReports.total', 'TOTAL')} ({products.length}{' '}
                          {t('salesReports.products', 'products')})
                        </td>
                        <td className="num">{productTotals.qty.toLocaleString()}</td>
                        <td className="num">{fmt(productTotals.revenue)}</td>
                        <td></td>
                        <td className="num">{uniqueInvoices}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* ── Menu items table — separate from Products since restaurant order
                lines reference MenuItem, a different catalog from Product ────── */}
            {menuItems.length > 0 && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <UtensilsCrossed size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    {t('salesReports.menuItems', 'Menu Items')}
                    <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--ims-text-muted)', marginInlineStart: 6 }}>
                      ({menuItems.length} {menuItems.length !== 1
                        ? t('salesReports.records', 'records')
                        : t('salesReports.record', 'record')})
                    </span>
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="ssr-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>#</th>
                        <th>{t('salesReports.productName', 'Item Name')}</th>
                        <th className="num">{t('salesReports.qtySold', 'Qty Sold')}</th>
                        <th className="num">{t('salesReports.revenue', 'Revenue')}</th>
                        <th className="num">{t('salesReports.avgUnitPrice', 'Avg Unit Price')}</th>
                        <th className="num">{t('salesReports.orderCount', '# Orders')}</th>
                        <th>{t('salesReports.colActions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMenuItems.map((item, idx) => (
                        <React.Fragment key={item.menuItemId}>
                          <tr>
                            <td className="ssr-row-num">{idx + 1}</td>
                            <td style={{ fontWeight: 600 }}>{item.menuItemName}</td>
                            <td className="num">{item.totalQuantity.toLocaleString()}</td>
                            <td className="num ssr-revenue-value">{fmt(item.totalRevenue)}</td>
                            <td className="num">{fmt(item.averagePrice)}</td>
                            <td className="num">{item.numberOfOrders}</td>
                            <td>
                              <button
                                className="ssr-btn-row ssr-btn-detail"
                                onClick={() => toggleMenuRow(item.menuItemId)}
                                title={t('salesReports.toggleInvoices', 'Show / hide invoices')}
                              >
                                {expandedMenuRows.has(item.menuItemId)
                                  ? <><ChevronUp size={11} /> {t('salesReports.hide', 'Hide')}</>
                                  : <><ChevronDown size={11} /> {t('salesReports.orders', 'Orders')}</>}
                              </button>
                            </td>
                          </tr>

                          {expandedMenuRows.has(item.menuItemId) && (
                            <tr className="ssr-detail-row">
                              <td colSpan={7}>
                                <div className="ssr-detail-inner">
                                  <h4 className="ssr-detail-title">
                                    {t('salesReports.invoicesContaining', 'Orders containing')} "{item.menuItemName}"
                                    &nbsp;—&nbsp;{item.orders.length}{' '}
                                    {item.orders.length !== 1
                                      ? t('salesReports.invoicesLower', 'orders')
                                      : t('salesReports.invoiceLower', 'order')}
                                  </h4>
                                  {item.orders.length === 0 ? (
                                    <p style={{ color: 'var(--ims-text-muted)', fontSize: 12 }}>
                                      {t('salesReports.noInvoices', 'No orders found.')}
                                    </p>
                                  ) : (
                                    <table className="ssr-lines-table">
                                      <thead>
                                        <tr>
                                          <th>{t('salesReports.colOrder', 'Order #')}</th>
                                          <th>{t('salesReports.colCustomer', 'Customer')}</th>
                                          <th>{t('salesReports.colDate', 'Date')}</th>
                                          <th>{t('salesReports.colStatus', 'Status')}</th>
                                          <th className="num">{t('salesReports.qty', 'Qty')}</th>
                                          <th className="num">{t('salesReports.lineTotal', 'Line Total')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {item.orders.map(o => (
                                          <tr key={o.orderId}>
                                            <td style={{ fontWeight: 600 }}>
                                              <span className="ssr-inv-num">{o.orderNumber}</span>
                                            </td>
                                            <td>{o.customerName || '—'}</td>
                                            <td>{new Date(o.orderDate).toLocaleDateString()}</td>
                                            <td>{o.status}</td>
                                            <td className="num">{o.quantity.toLocaleString()}</td>
                                            <td className="num" style={{ fontWeight: 600 }}>{fmt(o.lineTotal)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}

                                  <div className="ssr-detail-totals">
                                    <span>
                                      {t('salesReports.totalQty', 'Total qty')}:{' '}
                                      <strong>{item.totalQuantity.toLocaleString()}</strong>
                                    </span>
                                    <span className="ssr-detail-total-grand">
                                      {t('salesReports.totalRevenue', 'Total revenue')}:{' '}
                                      <strong>{fmt(item.totalRevenue)}</strong>
                                    </span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td></td>
                        <td>
                          {t('salesReports.total', 'TOTAL')} ({menuItems.length}{' '}
                          {t('salesReports.menuItemsLower', 'menu items')})
                        </td>
                        <td className="num">{menuItemTotals.qty.toLocaleString()}</td>
                        <td className="num">{fmt(menuItemTotals.revenue)}</td>
                        <td></td>
                        <td className="num">{menuItemTotals.orders}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* ── Top 5 by Revenue breakdown ──────────────────────── */}
            {sortedProducts.length > 1 && (
              <>
                <span className="ssr-section-title">
                  {t('salesReports.top5ByRevenue', 'Top 5 by Revenue')}
                </span>
                <div className="ssr-breakdown-grid">
                  {sortedProducts.slice(0, 5).map((p, idx) => (
                    <div key={p.productId} className="ssr-breakdown-card ssr-breakdown-rank">
                      <div className="ssr-breakdown-label">#{idx + 1}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ims-text-main)', marginBottom: 4 }}>
                        {p.productName}
                      </div>
                      <div className="ssr-breakdown-sub">{p.productSKU}</div>
                      <div className="ssr-breakdown-value" style={{ fontSize: '1.5rem' }}>
                        {fmt(p.totalRevenue)}
                      </div>
                      <div className="ssr-breakdown-pct">
                        {p.totalQuantity.toLocaleString()} {t('salesReports.units', 'units')}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

      </div>
    </Layout>
  );
};

export default SalesByProductReportPage;
